// src/components/modals/PurchaseBudgetModal.jsx
//
// Lets a school admin top up a joined program's coupon budget with
// real money: pick an amount and a payment method (credit card,
// PayPal, or bank transfer), then either complete payment in a popup
// (card/PayPal) or get bank details + a reference code to transfer
// against (bank transfer — confirmed later by an admin, not instant).
//
// Visual language (shell, field styles, buttons) matches
// CreateLessonModal.jsx / IssueCouponsModal.jsx — rounded-3xl sheet,
// gradient header with an icon tile, InputField with a floating focus
// ring, card-style method picker, and a sticky footer with a gradient
// primary button — so all three modals feel like the same product.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useUser } from '../../contexts/UserContext';
import { useSchoolPrograms } from '../../contexts/SchoolProgramsContext';

/* ── Module-level icon constants ─────────────────────────────── */
const IconWallet = (
  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 7H5a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2v-9a2 2 0 00-2-2z" />
    <path d="M16 3H8a2 2 0 00-2 2v2h12V5a2 2 0 00-2-2z" />
    <circle cx="17" cy="13" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);
const IconMoney = (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm.5 15.5v1h-1v-1c-1.4-.1-2.6-.7-3.5-1.7l1-1c.7.8 1.6 1.2 2.7 1.3.9 0 2-.5 2-1.6 0-1.1-1.1-1.5-2.3-1.9-1.5-.5-3.1-1.1-3.1-3 0-1.4 1.1-2.5 2.7-2.7V6h1v.9c1.1.1 2 .6 2.7 1.4l-1 1c-.5-.6-1.2-1-2.2-1-.8 0-1.7.4-1.7 1.3 0 .9.9 1.2 2.2 1.6 1.6.5 3.3 1.2 3.3 3.3 0 1.6-1.2 2.7-2.8 3z" />
  </svg>
);
const IconCard = (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </svg>
);
const IconPaypal = (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.4 20.6h2.9l.5-3.2h2c3.6 0 6.2-1.8 6.8-5.3.5-2.9-.8-4.7-3.5-5.3 1 .9 1.3 2.2 1 3.9-.5 3-2.7 4.3-5.7 4.3H9.9l-.8 5.1-1.7.5zM10.3 3.4H15c2.7 0 4.4 1.5 3.9 4.3-.5 3.1-2.7 4.6-5.9 4.6h-2l-.9 5.7H6.7L9 3.9l1.3-.5z" />
  </svg>
);
const IconBank = (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18M4 10h16M12 3L2 9h20L12 3zM5 10v9M9.5 10v9M14.5 10v9M19 10v9" />
  </svg>
);

const METHODS = [
  { id: 'card', label: 'כרטיס אשראי', sub: 'תשלום מיידי', icon: IconCard },
  { id: 'paypal', label: 'PayPal', sub: 'תשלום מיידי', icon: IconPaypal },
  { id: 'bank_transfer', label: 'העברה בנקאית', sub: 'אישור ידני', icon: IconBank },
];

/* ── Shared field helpers (same pattern as CreateLessonModal) ──── */
const FieldLabel = ({ htmlFor, required, focused, hasValue, children }) => (
  <label
    htmlFor={htmlFor}
    className={`block text-xs font-bold uppercase tracking-wide mb-1.5 text-right transition-colors duration-150
                ${focused || hasValue ? 'text-indigo-500' : 'text-gray-500'}`}
  >
    {children}{' '}
    {required && <span className="text-red-400 normal-case font-normal">*</span>}
  </label>
);

const InputField = ({ label, name, type = 'text', placeholder, value, onChange, required = false, icon, dir = 'rtl', ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div className="min-w-0">
      <FieldLabel htmlFor={name} required={required} focused={focused} hasValue={!!value}>
        {label}
      </FieldLabel>
      <div className="relative flex items-center">
        {icon && (
          <span className={`absolute right-3 pointer-events-none transition-colors duration-150 ${focused ? 'text-indigo-400' : 'text-gray-400'}`}>
            {icon}
          </span>
        )}
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          dir={dir}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          className={`block w-full min-w-0 h-10 ${icon ? 'pr-9' : 'pr-3'} pl-3 text-base sm:text-sm rounded-xl border bg-white text-gray-800 text-right
                      outline-none transition-all duration-150 appearance-none
                      ${focused ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-gray-200 hover:border-indigo-300'}`}
          {...props}
        />
      </div>
    </div>
  );
};

const formatMoney = (v) => `₪${Number(v || 0).toLocaleString('he-IL', { maximumFractionDigits: 2 })}`;

const PurchaseBudgetModal = ({ program, onClose, onPurchased }) => {
  const { showAlert } = useUser();
  const { purchaseBudget, uploadBudgetReceipt, fetchMyPrograms } = useSchoolPrograms();

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('card');
  const [submitting, setSubmitting] = useState(false);
  const [bankResult, setBankResult] = useState(null); // set after a bank_transfer purchase
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptSubmitted, setReceiptSubmitted] = useState(false);
  const popupRef = useRef(null);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const close = () => onCloseRef.current?.();

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || data.action !== 'close') return;
      if (data.status === 'success') {
        showAlert?.('התקציב עודכן בהצלחה', 'success');
        fetchMyPrograms();
        onPurchased?.(data.payload);
        onClose();
      } else {
        showAlert?.(data.message || 'התשלום לא הושלם', 'warning');
      }
      setSubmitting(false);
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = Number(amount);
    if (!value || value <= 0) {
      showAlert?.('יש להזין סכום גדול מאפס', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const result = await purchaseBudget(program.id, { amount: value, method });

      // Some error responses from this endpoint come back with an
      // HTTP success status but {ok:false, error:"..."} in the body
      // (e.g. the phone/email-verification check) — apiFetch only
      // throws on a non-2xx status, so those slip through as a
      // "successful" result. Check for that explicitly and show the
      // server's actual reason, instead of falling through to the
      // generic "no payment link" message below.
      if (result?.ok === false) {
        showAlert?.(result.error || 'יצירת בקשת התשלום נכשלה', 'error');
        setSubmitting(false);
        return;
      }

      if (result.method === 'bank_transfer') {
        setBankResult(result);
        setSubmitting(false);
        return;
      }

      const url = result.paymentUrl || result.approvalUrl;
      if (!url) {
        showAlert?.('לא התקבל קישור לתשלום, נסו שוב', 'error');
        setSubmitting(false);
        return;
      }

      popupRef.current = window.open(url, 'gpn-budget-payment', 'width=480,height=720');
      if (!popupRef.current) {
        showAlert?.('החלון הקופץ נחסם — יש לאפשר חלונות קופצים ולנסות שוב', 'warning');
        setSubmitting(false);
      }
      // submitting stays true until the popup posts back via
      // window.message (handled above) or the school gives up.
    } catch (err) {
      showAlert?.(err.message || 'יצירת בקשת התשלום נכשלה', 'error');
      setSubmitting(false);
    }
  };

  const handleReceiptSubmit = async () => {
    if (!bankResult?.purchaseId || !receiptFile) {
      showAlert?.('יש לבחור קובץ PDF של אישור ההעברה', 'warning');
      return;
    }
    setUploadingReceipt(true);
    try {
      await uploadBudgetReceipt(bankResult.purchaseId, receiptFile);
      setReceiptSubmitted(true);
      showAlert?.('אישור ההעברה נשלח, ממתין לבדיקת מנהל המערכת', 'success');
    } catch (err) {
      showAlert?.(err.message || 'העלאת אישור ההעברה נכשלה', 'error');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const submitLabel = method === 'bank_transfer' ? 'קבלת פרטי העברה בנקאית' : 'מעבר לתשלום';

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="purchase-budget-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]" onClick={close} />

      <div
        className="relative z-[1] w-full sm:max-w-md bg-white
                   rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20
                   flex flex-col max-h-[96dvh] sm:max-h-[90vh] overflow-hidden"
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-7 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              {IconWallet}
            </div>
            <div className="min-w-0">
              <h2 id="purchase-budget-title" className="text-gray-800 font-bold text-base sm:text-lg leading-tight">
                רכישת תקציב
              </h2>
              <p className="text-gray-500 text-xs mt-0.5 truncate">
                {program.programName ? `תוכנית: ${program.programName}` : 'הוספת תקציב לתוכנית'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="סגור"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        {bankResult ? (
          <>
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 sm:px-7 py-5 space-y-4">
              <p className="text-sm text-gray-600">{bankResult.message}</p>

              <div className="rounded-2xl bg-gray-50 border border-gray-100 p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">בנק</span>
                  <span className="font-semibold text-gray-800">{bankResult.bankDetails.bankName || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">סניף</span>
                  <span className="font-semibold text-gray-800">{bankResult.bankDetails.branch || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">מספר חשבון</span>
                  <span className="font-semibold font-mono text-gray-800">{bankResult.bankDetails.accountNumber || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">שם חשבון</span>
                  <span className="font-semibold text-gray-800">{bankResult.bankDetails.accountName || '—'}</span>
                </div>
                {bankResult.bankDetails.iban && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">IBAN</span>
                    <span className="font-semibold font-mono text-gray-800">{bankResult.bankDetails.iban}</span>
                  </div>
                )}
              </div>

              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4">
                <p className="text-amber-800 text-sm">קוד אסמכתא לציון בהעברה:</p>
                <p className="font-mono text-lg font-bold text-amber-900 mt-0.5">{bankResult.referenceCode}</p>
              </div>

              <p className="text-xs text-gray-400">
                סכום: {formatMoney(bankResult.amount)} · התקציב יעודכן לאחר אישור ההעברה על ידי המנהל.
              </p>

              <div className="rounded-2xl border-2 border-gray-200 p-4 space-y-2.5">
                {receiptSubmitted ? (
                  <p className="text-sm text-emerald-700 font-semibold flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    אישור ההעברה נשלח וממתין לבדיקה
                  </p>
                ) : (
                  <>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide">
                      לאחר ביצוע ההעברה בפועל — יש לצרף כאן את אישור ההעברה שהתקבל מהבנק (PDF)
                    </label>
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                      className="w-full text-xs text-gray-600 file:mr-2 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 file:px-3 file:py-1.5 file:text-xs file:font-semibold"
                    />
                    <button
                      type="button"
                      onClick={handleReceiptSubmit}
                      disabled={uploadingReceipt || !receiptFile}
                      className="w-full h-10 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700
                                 shadow-md shadow-emerald-100 transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                    >
                      {uploadingReceipt ? 'שולח…' : 'שליחת אישור ההעברה'}
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex items-center justify-end">
              <button
                type="button"
                onClick={close}
                className="h-10 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500
                           hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
              >
                סגירה
              </button>
            </div>
          </>
        ) : (
          <>
            <form
              id="purchase-budget-form"
              onSubmit={handleSubmit}
              className="flex-1 overflow-y-auto overscroll-contain px-5 sm:px-7 py-5"
            >
              <div className="space-y-5">
                <InputField
                  label="סכום לרכישה (₪)"
                  name="amount"
                  type="number"
                  min="0"
                  step="0.01"
                  dir="ltr"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="לדוגמה: 5000"
                  required
                  icon={IconMoney}
                />

                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2.5 text-right">
                    אמצעי תשלום <span className="text-red-400 normal-case font-normal">*</span>
                  </p>
                  <div className="grid grid-cols-1 gap-2.5">
                    {METHODS.map((m) => (
                      <label key={m.id} className="cursor-pointer">
                        <input
                          type="radio"
                          name="method"
                          value={m.id}
                          checked={method === m.id}
                          onChange={() => setMethod(m.id)}
                          className="sr-only"
                        />
                        <div
                          className={`flex items-center gap-3 p-3.5 rounded-2xl border-2 transition-all duration-150
                                      ${method === m.id ? 'border-indigo-400 bg-indigo-50 shadow-sm shadow-indigo-100' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                        >
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                              method === m.id ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-500'
                            }`}
                          >
                            {m.icon}
                          </div>
                          <div className="min-w-0">
                            <p className={`text-sm font-bold ${method === m.id ? 'text-indigo-800' : 'text-gray-700'}`}>{m.label}</p>
                            <p className="text-[11px] text-gray-400">{m.sub}</p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </form>

            <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={close}
                className="h-10 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500
                           hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
              >
                ביטול
              </button>
              <button
                type="submit"
                form="purchase-budget-form"
                disabled={submitting}
                className="h-10 px-6 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 text-white text-sm font-bold
                           shadow-md shadow-indigo-200 hover:shadow-lg hover:shadow-indigo-300 hover:-translate-y-px
                           disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0
                           transition-all duration-150 flex items-center gap-2"
              >
                {submitting && (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {submitting ? 'ממתין לתשלום…' : submitLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PurchaseBudgetModal;